import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import tw from 'twin.macro'
import { WRAP_WIDTH, WRAP_HEIGHT, CROP_WIDTH, CROP_HEIGHT } from '../_shared/ImageConstant'
import { getCropParameters } from './getCropParameters'

function Cropper(props) {
  const { selectedImages, setCropResults, tempCropPositions, setTempCropPositions, setDrag } = props
  const currentImage = selectedImages[0]

  const cropHeight = CROP_HEIGHT
  const cropWidth = CROP_WIDTH

  const paddingX = (WRAP_WIDTH - cropWidth) / 2
  const paddingY = (WRAP_HEIGHT - cropHeight) / 2

  const bgRef = useRef(null)
  const bgImageRef = useRef(null)
  const croppedRef = useRef(null)
  const topRef = useRef(null)

  const croppedBgPositionX = useRef(null)
  const croppedBgPositionY = useRef(null)
  const bgTop = useRef(null)
  const bgLeft = useRef(null)

  useEffect(() => {
    const bg = bgRef.current
    const bgImage = bgImageRef.current
    const cropped = croppedRef.current
    const top = topRef.current

    const defaultPosition = {
      bgLeft: 0,
      bgTop: 0,
      croppedBgPositionY: '50%',
      croppedBgPositionX: '50%',
    }

    if (currentImage) {
      // 先拿原圖長寬
      const originalWidth = currentImage.originalSize.width
      const originalHeight = currentImage.originalSize.height

      let bgImageHeight = 0
      let bgImageWidth = 0
      let direction = null

      // 根據圖片方向，設定 bgImage 的長寬, 以及取得原圖裁切的長寬
      // 四捨五入到小數第二位
      const roundedRatio = (ratio: number) => Math.round((ratio + Number.EPSILON) * 100) / 100
      const originalImageRatio = roundedRatio(originalHeight / originalWidth)
      const cropRatio = roundedRatio(cropHeight / cropWidth)
      if (originalImageRatio <= cropRatio) {
        direction = 'horizontal'
        bgImageHeight = cropHeight
        bgImageWidth = originalWidth * (cropHeight / originalHeight)
      } else {
        direction = 'vertical'
        bgImageWidth = cropWidth
        bgImageHeight = originalHeight * (cropWidth / originalWidth)
      }
      bgImage.style.height = direction === 'horizontal' ? `${bgImageHeight}px` : 'auto'
      bgImage.style.width = direction === 'horizontal' ? 'auto' : `${bgImageWidth}px`

      const initialCroppedBgPositionX = -((bgImageWidth - WRAP_WIDTH) / 2 + paddingX)
      const initialCroppedBgPositionY = -((bgImageHeight - WRAP_HEIGHT) / 2 + paddingY)

      // 設定會移動的元素的初始位置
      const cropPosition = currentImage.cropPosition
      const tempCropPosition = tempCropPositions[0]
      let positionConfig
      if (tempCropPosition) {
        // 進入裁切畫面後，有對此圖做裁切動作
        positionConfig = tempCropPosition
      } else if (cropPosition) {
        // 進入裁切畫面前，曾經對此圖做過裁切動作且儲存
        positionConfig = cropPosition
      } else {
        positionConfig = defaultPosition
      }
      const pxFixer = (style: string | number) => {
        return style === '50%' ? style : `${style}px`
      }
      bg.style.left = pxFixer(positionConfig.bgLeft)
      bg.style.top = pxFixer(positionConfig.bgTop)
      cropped.style.backgroundPositionY = pxFixer(positionConfig.croppedBgPositionY)
      cropped.style.backgroundPositionX = pxFixer(positionConfig.croppedBgPositionX)
      top.style.top = pxFixer(paddingY)
      top.style.left = pxFixer(paddingX)

      const dragging = (onMouseDownEvent: MouseEvent) => {
        onMouseDownEvent.preventDefault()

        let moveX = 0,
          moveY = 0,
          prevX = 0,
          prevY = 0

        // get the mouse cursor position at startup:
        prevX = onMouseDownEvent.clientX
        prevY = onMouseDownEvent.clientY

        document.onmousemove = (e) => {
          moveX = prevX - e.clientX
          moveY = prevY - e.clientY
          prevX = e.clientX
          prevY = e.clientY
          // set the element's new position:
          if (originalImageRatio !== cropRatio) {
            if (direction === 'vertical') {
              const tempBgTop = bg.offsetTop - moveY
              const tempCroppedBgPositionY = Math.floor(tempBgTop + initialCroppedBgPositionY)

              if (
                tempCroppedBgPositionY <= 0 &&
                tempCroppedBgPositionY >= -(bgImageHeight - cropHeight)
              ) {
                croppedBgPositionY.current = tempCroppedBgPositionY
                croppedBgPositionX.current = 0
                cropped.style.backgroundPositionY = `${croppedBgPositionY.current}px`
                bgTop.current = tempBgTop
                bg.style.top = `${bgTop.current}px`
              }
            } else {
              const tempBgLeft = bg.offsetLeft - moveX
              const tempCroppedBgPositionX = Math.floor(tempBgLeft + initialCroppedBgPositionX)

              if (
                tempCroppedBgPositionX <= 0 &&
                tempCroppedBgPositionX >= -(bgImageWidth - cropWidth)
              ) {
                croppedBgPositionX.current = tempCroppedBgPositionX
                croppedBgPositionY.current = 0
                cropped.style.backgroundPositionX = `${croppedBgPositionX.current}px`
                bgLeft.current = tempBgLeft
                bg.style.left = `${bgLeft.current}px`
              }
            }
          }
        }

        document.onmouseup = () => {
          //stop moving when mouse button is released:
          document.onmouseup = null
          document.onmousemove = null
          setDrag(true)

          let updatedPosition = {}
          // 在儲存前，記下移動的位置
          setTempCropPositions((prevArray) => {
            updatedPosition = {
              ...prevArray[0],
              id: currentImage.id,
              croppedBgPositionX: croppedBgPositionX.current,
              croppedBgPositionY: croppedBgPositionY.current,
              bgTop: direction === 'vertical' ? bgTop.current : 0,
              bgLeft: direction !== 'vertical' ? bgLeft.current : 0,
            }
            return [updatedPosition]
          })

          setCropResults((prevArray) => {
            // 將裁切點還原成相對於圖片原尺寸的位置
            const calculateCropParametersForAllRatiosInput = {
              cropPosition: updatedPosition,
              originalWidth,
              originalHeight,
              bgImageWidth,
              bgImageHeight,
              cropWidth,
              cropHeight,
            }
            const crop = getCropParameters(calculateCropParametersForAllRatiosInput)
            const updated = {
              ...prevArray[0],
              id: currentImage.id,
              crop,
            }

            return [updated]
          })
        }
      }
      top.addEventListener('mousedown', dragging)
    }
  }, [
    cropWidth,
    cropHeight,
    currentImage,
    paddingX,
    paddingY,
    setCropResults,
    setTempCropPositions,
    tempCropPositions,
    setDrag,
  ])

  return (
    <CropArea
      wrapWidth={WRAP_WIDTH}
      wrapHeight={WRAP_HEIGHT}
      cropWidth={cropWidth}
      cropHeight={cropHeight}
    >
      <div className="wrap">
        <div className="bg" ref={bgRef}>
          <img ref={bgImageRef} src={currentImage?.originalUrl} />
        </div>
        <div className="mask" />
        <Cropped
          ref={croppedRef}
          className="cropped"
          draggable="false"
          imageSrc={currentImage?.originalUrl}
          wrapWidth={WRAP_WIDTH}
          wrapHeight={WRAP_HEIGHT}
          cropWidth={cropWidth}
          cropHeight={cropHeight}
        />
        <div className="top" ref={topRef} />
      </div>
    </CropArea>
  )
}

const CropArea = styled.div<{
  wrapWidth?: number
  wrapHeight?: number
  cropWidth?: number
  cropHeight?: number
}>`
  width: 100%;
  height: ${(props) => `${props.wrapHeight}px`};
  .wrap {
    ${tw`rounded-br-lg rounded-bl-lg`}
    width: ${(props) => `${props.wrapWidth}px`};
    height: 100%;
    position: relative;
    overflow: hidden;
  }
  .bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .bg img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    max-width: initial;
  }
  .mask {
    background-color: rgba(0, 0, 0, 0.5);
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
  .top {
    position: absolute;
    top: ${(props) => `${(props.wrapHeight - props.cropHeight) / 2}px`};
    left: ${(props) => `${(props.wrapWidth - props.cropWidth) / 2}px`};
    width: ${(props) => `${props.cropWidth}px`};
    height: ${(props) => `${props.cropHeight}px`};
    ${tw`cursor-move`}
  }
`

const Cropped = styled.div<{
  imageSrc?: string
  wrapWidth?: number
  wrapHeight?: number
  cropWidth?: number
  cropHeight?: number
}>`
  position: absolute;
  top: ${(props) => `${(props.wrapHeight - props.cropHeight) / 2}px`};
  left: ${(props) => `${(props.wrapWidth - props.cropWidth) / 2}px`};
  width: ${(props) => `${props.cropWidth}px`};
  height: ${(props) => `${props.cropHeight}px`};
  background-image: ${(props) => `url(${props.imageSrc})`};
  background-position-x: 50%;
  background-position-y: 50%;
  background-size: cover;
  border: 1px solid rgba(255, 255, 255, 0.8);
`

export default Cropper
